// ─── backoffice-api fetch client ───────────────────────────────────────────
//
// Shared low-level fetch wrapper for backoffice-api's `/v1/*` endpoints. Owns
// the `cpg_backoffice_token` / `cpg_backoffice_refresh_token` localStorage
// keys: setTokens() persists both (auth.ts calls this from login()),
// getToken()/getRefreshToken() read them back, clearTokens() removes both
// (auth.ts's logout() calls this, and the shell's sign-out button goes
// through logout()), and authRequest() attaches the access token as a Bearer
// Authorization header for authenticated endpoints (merchants, invoices,
// high-risk queue, admins, me) — on a 401 it transparently exchanges the
// refresh token for a new pair via POST /auth/refresh and retries the
// request once before giving up, so an expired access token never bounces
// the admin to /login as long as their refresh token is still valid.

const TOKEN_KEY = 'cpg_backoffice_token';
const REFRESH_TOKEN_KEY = 'cpg_backoffice_refresh_token';

export const API_URL =
  (import.meta.env.VITE_BACKOFFICE_API_URL as string | undefined) ??
  'http://localhost:3002/v1';

/** Standard error body every backend service returns (see @cpg/modules ApiErrorBody). */
interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

/**
 * Thrown by the functions below on any non-2xx response. Carries the HTTP
 * status plus the backend's `{ code, message }` error body so callers can
 * branch on status (401/403 on login/me, 409/422 on later mutating
 * endpoints) and show the right message.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getToken(): string | null {
  return window.localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return window.localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(token: string, refreshToken: string): void {
  window.localStorage.setItem(TOKEN_KEY, token);
  window.localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Bare fetch + non-2xx-throws-ApiError, shared by `request()` (JSON body) and
 * `authRequestDownload()` (raw blob body, e.g. the Accountant CSV export) —
 * factored out so the two never duplicate the status-check/error-parsing
 * logic below.
 */
async function fetchRaw(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(
      res.status,
      body?.code ?? 'UNKNOWN_ERROR',
      body?.message ?? `Request failed with status ${res.status}`,
    );
  }

  return res;
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetchRaw(path, init);

  // 204 No Content (e.g. backoffice-api's tariff/tariff-rule DELETE routes)
  // has no body — res.json() throws on an empty string, so resolve straight
  // to undefined instead of attempting to parse one.
  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
}

/** Minimal shape of the /auth/refresh response — enough to update stored tokens. */
interface RefreshResponse {
  token: string;
  refreshToken: string;
}

// De-duped in-flight refresh so N concurrent 401s trigger exactly one
// POST /auth/refresh call — the rest await the same promise instead of each
// racing to rotate the (single-use) refresh token.
let refreshPromise: Promise<string | null> | null = null;

function refreshAccessToken(): Promise<string | null> {
  const currentRefreshToken = getRefreshToken();
  if (!currentRefreshToken) {
    return Promise.resolve(null);
  }
  if (!refreshPromise) {
    refreshPromise = request<RefreshResponse>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: currentRefreshToken }),
    })
      .then((data) => {
        setTokens(data.token, data.refreshToken);
        return data.token;
      })
      .catch(() => {
        clearTokens();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * `fetchRaw()` with the Authorization header attached when a token exists. A
 * 401 (expired/invalid access token) triggers exactly one silent refresh
 * attempt, then retries the original request once with the new access
 * token — if the refresh itself fails, the original 401 is re-thrown so the
 * caller's existing catch-and-redirect-to-/login still fires unchanged.
 * Shared by `authRequest()` (JSON) and `authRequestDownload()` (blob) so the
 * refresh-and-retry logic lives in exactly one place.
 */
async function authFetchRaw(path: string, init?: RequestInit): Promise<Response> {
  const token = getToken();
  try {
    return await fetchRaw(path, {
      ...init,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...init?.headers },
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        return fetchRaw(path, {
          ...init,
          headers: { Authorization: `Bearer ${newToken}`, ...init?.headers },
        });
      }
    }
    throw err;
  }
}

/** `authFetchRaw()` + JSON parsing — the authenticated counterpart to `request()`. */
export async function authRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetchRaw(path, init);
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

/** Filename from a `Content-Disposition: attachment; filename="..."` header, or `null` if absent/unparseable. */
function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename\*?=(?:"([^"]+)"|([^;]+))/i.exec(header);
  return match ? (match[1] ?? match[2])?.trim() ?? null : null;
}

export interface DownloadResult {
  blob: Blob;
  /** Filename the server suggested via `Content-Disposition`, or `null` if the header was absent/unparseable. */
  filename: string | null;
}

/**
 * `authFetchRaw()` + raw `Blob` body instead of JSON — for endpoints that
 * stream a file (e.g. `GET /v1/accountant/export`'s CSV) rather than
 * returning a JSON envelope. Reuses the exact same Bearer-attach +
 * refresh-and-retry plumbing as `authRequest()`, just without the
 * `res.json()` parse at the end.
 */
export async function authRequestDownload(path: string, init?: RequestInit): Promise<DownloadResult> {
  const res = await authFetchRaw(path, init);
  const blob = await res.blob();
  const filename = filenameFromContentDisposition(res.headers.get('Content-Disposition'));
  return { blob, filename };
}
